// Global packages and components
import { Fragment, ReactNode } from 'react'
import { useFloating } from '@floating-ui/react'
import { autoPlacement } from '@floating-ui/dom'
import {
  QuestionMarkCircleIcon,
  ExclamationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import { clsx } from 'clsx'

// Types
interface HelpPopoverProps {
  /**
   * Label
   */
  label?: string
  /**
   * Tooltip title
   */
  title?: string
  /**
   * Tooltip description
   */
  description: string | ReactNode
  /**
   * Tooltip description
   */
  size?: 'xs' | 'sm' | 'md' | 'lg'
  /**
   * Tooltip flip order
   */
  flip?: boolean
  /**
   * Colour options
   */
  colour?: 'teal-green' | 'vibrant-green' | 'red'
}

// Main export
const HelpPopover = ({
  label,
  title,
  description,
  size = 'sm',
  flip = false,
  colour = 'teal-green',
}: HelpPopoverProps) => {
  const { refs, placement, floatingStyles } = useFloating({
    placement: 'bottom-end',
    middleware: [
      autoPlacement({
        allowedPlacements: ['bottom-end', 'bottom-start', 'bottom'],
      }),
    ],
  })

  return (
    <Popover as={Fragment}>
      <>
        <PopoverButton
          ref={refs.setReference}
          className={clsx('focus:outline-none', {
            'flex items-center': label,
          })}
        >
          {label && (
            <span
              className={clsx({
                'text-xs': size == 'xs',
                'text-sm': size == 'sm',
                'text-md': size == 'md',
                'text-lg': size == 'lg',
                'order-2 pl-1': flip,
                'pe-2': !flip,
                'text-teal-green underline': colour == 'teal-green',
                'text-vibrant-green underline': colour == 'vibrant-green',
                'text-red-500': colour == 'red',
              })}
            >
              {label}
            </span>
          )}
          <div className={clsx('-mt-0.5', { 'order-1': flip })}>
            {colour == 'red' ? (
              <ExclamationCircleIcon
                className={clsx('text-red-500', {
                  'h-5 w-5': size == 'sm' || size == 'xs',
                  'h-6 w-6': size == 'md',
                  'h-8 w-8': size == 'lg',
                })}
              />
            ) : (
              <QuestionMarkCircleIcon
                className={clsx({
                  'h-5 w-5': size == 'sm' || size == 'xs',
                  'h-6 w-6': size == 'md',
                  'h-8 w-8': size == 'lg',
                  'text-teal-green': colour == 'teal-green',
                  'text-vibrant-green underline': colour == 'vibrant-green',
                })}
              />
            )}
          </div>
        </PopoverButton>
        <PopoverPanel
          className="z-30 w-[15rem] sm:w-[20rem]"
          ref={refs.setFloating}
          style={floatingStyles}
        >
          {({ close }) => (
            <div
              className={clsx('relative pt-7', {
                '-mr-5': placement == 'bottom-end',
                '-ml-5': placement == 'bottom-start',
              })}
            >
              <div
                className={clsx('absolute top-0 inline-block overflow-hidden', {
                  'right-0': placement == 'bottom-end',
                  'left-0': placement == 'bottom-start',
                  'left-2/4 -translate-x-2/4': placement == 'bottom',
                  'w-[3.8rem]': size == 'sm' || size == 'xs',
                  'w-[3.9rem]': size == 'md',
                  'w-[4.15rem]': size == 'lg',
                })}
              >
                <div className="w-15 h-11 origin-bottom-left rotate-45 transform bg-white" />
              </div>
              <div className="relative rounded-lg bg-white px-5 pb-5 shadow-md">
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => close()}
                    className={clsx(
                      '-mr-0.5 mb-1 mt-3 flex size-6 items-center justify-center rounded-full border border-neutral-400 focus:outline-none',
                      {
                        '-mr-0.5': size !== 'lg',
                        'mr-1': size == 'lg',
                      }
                    )}
                  >
                    <XMarkIcon className="size-4 text-neutral-400" />
                  </button>
                </div>
                {title && (
                  <p className="mb-2 text-sm">
                    <b>{title}</b>
                  </p>
                )}
                <p className="mb-0 text-sm">{description}</p>
              </div>
            </div>
          )}
        </PopoverPanel>
      </>
    </Popover>
  )
}

export default HelpPopover
